(function () {
  "use strict";

  const cfgEarly = window.__taxToggleConfig || {};
  if (cfgEarly.taxToggleEnabled === false) return;

  const STORAGE_KEY = "priceDisplayMode";
  const DEFAULT_TAX_RATE = 1.2;
  const FALLBACK_COUNTRY_TAX_RATES = {
    AT: 1.2,
    BE: 1.21,
    BG: 1.2,
    CY: 1.19,
    CZ: 1.21,
    DE: 1.19,
    DK: 1.25,
    EE: 1.22,
    ES: 1.21,
    FI: 1.255,
    FR: 1.2,
    GR: 1.24,
    HR: 1.25,
    HU: 1.27,
    IE: 1.23,
    IT: 1.22,
    LT: 1.21,
    LU: 1.17,
    LV: 1.21,
    MT: 1.18,
    NL: 1.21,
    PL: 1.23,
    PT: 1.23,
    RO: 1.19,
    SE: 1.25,
    SI: 1.22,
    SK: 1.23,
  };

  if (window.__taxToggleInstance) return;
  window.__taxToggleInstance = true;

  const CART_UPDATE_EVENT = "cart:update";
  const DISCOUNT_UPDATE_EVENT = "discount:update";
  /** Fired when variant HTML is swapped; product-price replaces price markup (resets tax label to Liquid default). */
  const VARIANT_UPDATE_EVENT = "variant:update";
  /** Emitted after collection/main section morph (e.g. facets, pagination). */
  const SECTION_RENDERED_EVENT = "section-rendered";

  /**
   * B2B trade cart is always ex-VAT and ignores the toggle. Private cellar (VIP) uses the
   * same `[data-b2b-cart-ui]` shell with `data-b2b-catalog-scope="vip"` and should follow
   * HT/TTC like the main storefront — EXCEPT the auto-managed fee lines (insurance + excise/
   * duty, `.b2b-cart-line--fee`): those variants have "charge tax" off, so the fee is a fixed
   * amount with no VAT component and must read the same in HT and TTC (never converted).
   */
  function isB2BCartExcludingTaxToggle(el) {
    if (!(el instanceof Element)) return false;
    if (el.closest(".b2b-cart-line--fee")) return true;
    const root = el.closest("[data-b2b-cart-ui]");
    if (!root) return false;
    const scope = (root.getAttribute("data-b2b-catalog-scope") || "")
      .trim()
      .toLowerCase();
    return scope !== "vip";
  }

  class TaxToggle {
    constructor() {
      this.taxContext = this.getTaxContext();
      this.taxRate = this.getTaxRate();
      this.currentMode = this.loadPreference();
      this.dropdownInitialized = false;
      this.isDropdownOpen = false;
      this.init();
    }

    init() {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", () => this.setup());
      } else {
        this.setup();
      }
    }

    setup() {
      this.currentMode = this.loadPreference();
      this.setupDropdown();
      this.setupCartSync();
      this.applyMode(this.currentMode, false);
      this.observeDOMChanges();
      this.setupTaxLabelResync();
      this.scheduleRevealTaxTogglePrices(this.currentMode);
      // Re-apply label/ARIA from this.currentMode only (do not re-read storage here — that
      // caused the toggle text to disagree with prices when reads raced or defaulted to TTC).
      [100, 300, 600, 1000, 1500, 2500].forEach((ms) =>
        setTimeout(() => this.updateDropdownUI(), ms)
      );

      window.addEventListener("storage", (e) => {
        if (e.key !== STORAGE_KEY || e.storageArea !== localStorage) return;
        const next = this.loadPreference();
        this.currentMode = next;
        this.applyMode(this.currentMode, false);
      });
    }

    loadPreference() {
      const configuredMode = this.getConfiguredDefaultMode();
      if (configuredMode) {
        return configuredMode;
      }
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw == null || raw === "") {
          return this.getDefaultMode();
        }
        const saved = String(raw).trim().toLowerCase();
        if (saved === "ttc" || saved === "ht") {
          if (String(raw) !== saved) {
            try {
              localStorage.setItem(STORAGE_KEY, saved);
            } catch (_) {
              /* ignore */
            }
          }
          return saved;
        }
        return this.getDefaultMode();
      } catch (e) {
        return this.getDefaultMode();
      }
    }

    savePreference(mode) {
      try {
        localStorage.setItem(STORAGE_KEY, mode);
      } catch (e) {
        console.warn("Failed to save preference");
      }
    }

    getTaxContext() {
      const cfg = window.__taxToggleConfig || {};
      const countryCode =
        (cfg.countryCode || document.body?.dataset?.countryCode || "")
          .toString()
          .trim()
          .toUpperCase();
      const marketHandle = (cfg.marketHandle || document.body?.dataset?.marketHandle || "")
        .toString()
        .trim();
      const configuredRates = cfg.taxRatesByCountry || {};
      return {
        countryCode,
        marketHandle,
        configuredRates,
        defaultTaxRate:
          typeof cfg.defaultTaxRate === "number" && cfg.defaultTaxRate > 0
            ? cfg.defaultTaxRate
            : DEFAULT_TAX_RATE,
      };
    }

    getTaxRate() {
      const { countryCode, configuredRates, defaultTaxRate } = this.taxContext;
      const configuredRate = Number(configuredRates[countryCode]);
      if (Number.isFinite(configuredRate) && configuredRate > 0) {
        return configuredRate;
      }
      const fallbackRate = Number(FALLBACK_COUNTRY_TAX_RATES[countryCode]);
      if (Number.isFinite(fallbackRate) && fallbackRate > 0) {
        return fallbackRate;
      }
      return defaultTaxRate;
    }

    getDefaultMode() {
      const configuredMode = this.getConfiguredDefaultMode();
      if (configuredMode) return configuredMode;
      // Countries with no known VAT multiplier default to ex-tax display.
      return this.taxRate > 1 ? "ttc" : "ht";
    }

    getConfiguredDefaultMode() {
      const configuredMode = (
        (window.__taxToggleConfig && window.__taxToggleConfig.defaultMode) ||
        ""
      )
        .toString()
        .trim()
        .toLowerCase();
      return configuredMode === "ht" || configuredMode === "ttc"
        ? configuredMode
        : "";
    }

    setupDropdown() {
      const containers = document.querySelectorAll(".tax-toggle-dropdown");

      if (!containers.length) {
        if (!this.dropdownInitialized) {
          setTimeout(() => this.setupDropdown(), 100);
        }
        return;
      }

      if (this.dropdownInitialized) return;
      this.dropdownInitialized = true;

      // Set initial UI state
      this.updateDropdownUI();

      // Bind each instance (desktop header + mobile drawer)
      containers.forEach((container) => {
        const button = container.querySelector(".tax-toggle-dropdown__trigger");
        const options = container.querySelector(".tax-toggle-dropdown__options");
        if (!button || !options) return;

        button.addEventListener("click", (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggleDropdown(container);
        });

        options.addEventListener(
          "mousedown",
          (e) => {
            const option = e.target.closest(".tax-toggle-dropdown__option");
            if (!option) return;
            e.preventDefault();
            e.stopPropagation();
            const value = option.getAttribute("data-value");
            if (value) this.selectOption(value);
          },
          true
        );
      });

      // Close dropdown on outside click (use click so it runs after mousedown)
      document.addEventListener("click", (e) => {
        if (!e.target.closest(".tax-toggle-dropdown")) {
          this.closeDropdown();
        }
      });

      // Close dropdown on escape key
      document.addEventListener("keydown", (e) => {
        if (e.key === "Escape" && this.openContainer) {
          this.closeDropdown();
        }
      });
    }

    toggleDropdown(container) {
      if (this.openContainer === container) {
        this.closeDropdown();
      } else {
        this.openDropdown(container);
      }
    }

    openDropdown(container) {
      const button = container?.querySelector(".tax-toggle-dropdown__trigger");
      const options = container?.querySelector(".tax-toggle-dropdown__options");

      if (!button || !options) return;

      this.closeDropdown();
      this.openContainer = container;
      this.isDropdownOpen = true;
      button.setAttribute("aria-expanded", "true");
      options.setAttribute("aria-hidden", "false");
    }

    closeDropdown() {
      if (!this.openContainer) return;

      const button = this.openContainer.querySelector(".tax-toggle-dropdown__trigger");
      const options = this.openContainer.querySelector(".tax-toggle-dropdown__options");

      if (button && options) {
        this.isDropdownOpen = false;
        button.setAttribute("aria-expanded", "false");
        options.setAttribute("aria-hidden", "true");
      }
      this.openContainer = null;
    }

    selectOption(value) {
      if (value !== "ht" && value !== "ttc") return;
      if (document.documentElement.classList.contains("tax-toggle-pending")) {
        return;
      }

      this.closeDropdown();

      if (this.currentMode !== value) {
        this.currentMode = value;
        this.savePreference(value);
      }
      this.updateDropdownUI();
      this.applyMode(this.currentMode, true);
    }

    updateDropdownUI() {
      let mode = this.currentMode;
      if (mode !== "ht" && mode !== "ttc") {
        mode = this.loadPreference();
        this.currentMode = mode;
      }

      const findOptionForMode = (container) => {
        const opts = container.querySelectorAll(".tax-toggle-dropdown__option");
        for (const opt of opts) {
          const v = (opt.getAttribute("data-value") || "").trim().toLowerCase();
          if (v === mode) return opt;
        }
        return null;
      };

      // Update all dropdown labels (handles duplicate headers e.g. mobile/desktop)
      document
        .querySelectorAll(".tax-toggle-dropdown")
        .forEach((container) => {
          const currentLabel = container.querySelector(".tax-toggle-dropdown__current");
          const selectedOption = findOptionForMode(container);
          if (currentLabel) {
            currentLabel.textContent = selectedOption
              ? selectedOption.textContent.trim()
              : mode.toUpperCase();
          }
        });

      // Update selected states for all options (handles duplicate dropdowns)
      document
        .querySelectorAll(".tax-toggle-dropdown__option")
        .forEach((option) => {
          const optValue = (option.getAttribute("data-value") || "")
            .trim()
            .toLowerCase();
          option.setAttribute(
            "aria-selected",
            optValue === mode ? "true" : "false"
          );
        });
    }

    /**
     * After cart morph/section refresh, Shopify sends new money strings. Clear derived
     * attributes so we re-parse HT base from the server-rendered text (same as product cards).
     */
    resetCartDerivedPriceAttributes() {
      const cartRoots = document.querySelectorAll(
        'cart-items-component, .cart-form, .cart-drawer, [data-b2b-cart-ui][data-b2b-catalog-scope="vip"]'
      );
      cartRoots.forEach((root) => {
        root.querySelectorAll("[data-original-price]").forEach((el) => {
          el.removeAttribute("data-original-price");
          el.removeAttribute("data-currency-after");
        });
        root.querySelectorAll("text-component[value]").forEach((el) => {
          const visible = this.sanitizeMoneyText(el.textContent || "");
          if (visible && /\d/.test(visible)) {
            el.setAttribute("value", visible);
          }
        });
      });
    }

    setupCartSync() {
      /** Cart lines / discounts change money nodes only — do not re-read storage here (avoids flipping mode). */
      const refreshCartPrices = () => {
        this._suppressCartPriceMutationUntil = Date.now() + 350;
        this.resetCartDerivedPriceAttributes();
        this.applyMode(this.currentMode, false);
      };
      /** Drawer open/close can fire without fresh server money; keep cached base prices to avoid compounding TTC. */
      const reapplyCartPrices = () => {
        this.applyMode(this.currentMode, false);
      };

      /** Section HTML was morphed before cart:update (see component-cart-items). */
      const cartSectionsAlreadyApplied = (event) => {
        const sections = event?.detail?.data?.sections;
        return (
          sections &&
          typeof sections === "object" &&
          Object.keys(sections).length > 0
        );
      };

      /**
       * When cart section HTML is already in the DOM, reset derived prices once.
       * Re-applying on stale data-original-price before morph caused HT/TTC flicker.
       * Async updates (discount code, B2B without sections) keep the delayed refresh.
       */
      const scheduleCartPriceRefresh = (event) => {
        if (this._cartRefreshTimer) {
          clearTimeout(this._cartRefreshTimer);
          this._cartRefreshTimer = null;
        }

        if (cartSectionsAlreadyApplied(event)) {
          this._awaitingCartServerPrices = false;
          refreshCartPrices();
          return;
        }

        reapplyCartPrices();
        this._awaitingCartServerPrices = true;
        this._cartRefreshTimer = setTimeout(() => {
          this._cartRefreshTimer = null;
          this._awaitingCartServerPrices = false;
          refreshCartPrices();
        }, 1600);
      };

      document.addEventListener(CART_UPDATE_EVENT, (event) => {
        scheduleCartPriceRefresh(event);
      });
      document.addEventListener(DISCOUNT_UPDATE_EVENT, (event) => {
        scheduleCartPriceRefresh(event);
      });

      const refreshDrawerWhenReady = () => {
        reapplyCartPrices();
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => {
            requestAnimationFrame(reapplyCartPrices);
          });
        }
      };

      document.addEventListener(
        "dialog:open",
        (e) => {
          const t = e.target;
          if (!(t instanceof Element)) return;
          if (!t.matches("cart-drawer-component")) return;
          refreshDrawerWhenReady();
        },
        true
      );
      document.addEventListener(
        "dialog:close",
        (e) => {
          const t = e.target;
          if (!(t instanceof Element)) return;
          if (!t.matches("cart-drawer-component")) return;
          refreshDrawerWhenReady();
        },
        true
      );

      window.__taxToggleRefreshCartPrices = refreshCartPrices;
    }

    /**
     * product-price.js replaces [ref=priceContainer] on variant:update with fresh Liquid HTML
     * (tax span defaults to "TVA incluse"). Section morphs can do the same. Re-sync after those
     * run so labels match this.currentMode without waiting only on the generic mutation debounce.
     */
    setupTaxLabelResync() {
      const applyAfterPaint = () => {
        this.applyMode(this.currentMode, false);
      };

      document.addEventListener(
        VARIANT_UPDATE_EVENT,
        () => {
          setTimeout(() => applyAfterPaint(), 0);
        },
        false
      );

      if (typeof window !== "undefined") {
        let sectionTimer = null;
        window.addEventListener(SECTION_RENDERED_EVENT, () => {
          if (sectionTimer) clearTimeout(sectionTimer);
          sectionTimer = setTimeout(() => {
            sectionTimer = null;
            applyAfterPaint();
          }, 80);
        });
      }
    }

    applyMode(mode, animate = false) {
      if (mode === "ht" || mode === "ttc") {
        this.currentMode = mode;
      }
      this.updateAllPrices(mode, animate);
      const syncLabels = () => this.updateAllTaxLabels();
      syncLabels();
      requestAnimationFrame(() => syncLabels());
      if (animate) {
        setTimeout(syncLabels, 220);
      }
    }

    /** Mode used for tax *wording* (TVA incluse / HT) — always follow applied prices. */
    resolveLabelMode() {
      if (this.currentMode === "ht" || this.currentMode === "ttc") {
        return this.currentMode;
      }
      const fromStorage = this.loadPreference();
      this.currentMode = fromStorage;
      return fromStorage;
    }

    /**
     * Remove pending only after applyMode's deferred label passes (cart summary uses rAF×2).
     * Sync dropdown before unhide so HT preference never flashes the Liquid default "TTC" label.
     * Uses the same mode as the initial applyMode (boot) so a flaky localStorage read cannot
     * mix HT prices with TTC labels mid-reveal.
     */
    scheduleRevealTaxTogglePrices(bootMode) {
      const mode =
        bootMode === "ht" || bootMode === "ttc" ? bootMode : this.loadPreference();
      if (mode === "ht" || mode === "ttc") {
        this.currentMode = mode;
      }
      const done = () => {
        this.updateDropdownUI();
        document.documentElement.classList.remove("tax-toggle-pending");
      };
      try {
        if (typeof requestAnimationFrame === "function") {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.updateAllTaxLabels();
              requestAnimationFrame(() => {
                requestAnimationFrame(done);
              });
            });
          });
        } else {
          this.updateAllTaxLabels();
          done();
        }
      } catch (e) {
        done();
      }
      setTimeout(() => {
        if (document.documentElement.classList.contains("tax-toggle-pending")) {
          done();
        }
      }, 4000);
    }

    updateAllTaxLabels() {
      const mode = this.resolveLabelMode();
      document
        .querySelectorAll(
          "[data-tax-label-ttc][data-tax-label-ht]:not([data-cart-summary-tax-label])"
        )
        .forEach((el) => {
          if (isB2BCartExcludingTaxToggle(el)) return;
          const ttc = el.getAttribute("data-tax-label-ttc") || "inc. VAT";
          const ht = el.getAttribute("data-tax-label-ht") || "Ex. VAT";
          el.textContent = mode === "ttc" ? ttc : ht;
        });

      this.syncCartSummaryTaxLabels();
    }

    /**
     * Cart estimated total label must match the total text-component (morph / value attr timing).
     * Reads mode when the rAF runs so a stale closure cannot re-apply "TVA incluse" after HT.
     */
    syncCartSummaryTaxLabels() {
      const apply = () => {
        const mode = this.resolveLabelMode();
        document.querySelectorAll("[data-cart-summary-tax-label]").forEach((label) => {
          if (isB2BCartExcludingTaxToggle(label)) return;
          const ttc = label.getAttribute("data-tax-label-ttc") || "inc. VAT";
          const ht = label.getAttribute("data-tax-label-ht") || "Ex. VAT";
          label.textContent = mode === "ttc" ? ttc : ht;
        });
      };
      if (typeof requestAnimationFrame === "function") {
        requestAnimationFrame(() => requestAnimationFrame(apply));
      } else {
        apply();
      }
    }

    updateAllPrices(mode, animate) {

      const selectors = [
        ".price:not(.compare-at-price)",
        "span.compare-at-price",
        ".cart-items__price text-component",
        ".cart-drawer .cart-items__price text-component",
        '[data-b2b-cart-ui][data-b2b-catalog-scope="vip"] .cart-items__price',
        // Mirror of the line total shown in the middle column (aria-hidden duplicate). Same HT source
        // value as .cart-items__price above, so it must get the same HT/TTC transform — otherwise it
        // stays ex-VAT (e.g. €340) while the right-column price shows inc-VAT (€408).
        '[data-b2b-cart-ui][data-b2b-catalog-scope="vip"] .b2b-cart-line__inline-price',
        "td.cart-items__details > div > span:not(.visually-hidden):not(.compare-at-price)",
        "td.cart-items__details > div > s.compare-at-price",
        ".cart__total-value",
        ".cart__original-total-value",
        ".cart__discount-value",
        ".cart__subtotal-value",
        "table.cart-items__table caption span",
        ".price .money",
        ".product-price .money",
        '[class*="price"] .money',
      ];

      let updatedCount = 0;
      const seen = new Set();

      selectors.forEach((selector) => {
        const elements = document.querySelectorAll(selector);
        elements.forEach((el) => {
          if (seen.has(el)) return;
          seen.add(el);
          if (this.updatePrice(el, mode, animate)) {
            updatedCount++;
          }
        });
      });

    }

    /** NBSP + narrow NBSP; keeps money strings parseable. */
    sanitizeMoneyText(text) {
      if (text == null || typeof text !== "string") return "";
      return text
        .replace(/\u00a0/g, " ")
        .replace(/\u202f/g, " ")
        .trim();
    }

    /**
     * Convert a tax-included amount from Liquid/Shopify into HT base for toggle math.
     * When server money is already ex-VAT (cart.taxes_included false), do not call this.
     */
    toTaxExcludedBase(amount) {
      const n = typeof amount === "number" ? amount : parseFloat(amount);
      if (!Number.isFinite(n) || n <= 0 || this.taxRate <= 1) return amount;
      return Math.round((n / this.taxRate) * 100) / 100;
    }

    /** Read data-cart-taxes-included stamped on cart roots (theme Ajax cart or B2B UI). */
    readCartTaxesIncludedStamp(root) {
      if (!(root instanceof Element)) return null;
      const stamped = root.getAttribute("data-cart-taxes-included");
      if (stamped === "true") return true;
      if (stamped === "false") return false;
      return null;
    }

    /**
     * Whether money rendered by Shopify for this node already includes VAT.
     * Catalog + cart use cart.taxes_included for the current market (see theme.liquid).
     */
    serverMoneyIncludesTax(element) {
      const themeCart = element?.closest?.("cart-items-component");
      const themeStamp = this.readCartTaxesIncludedStamp(themeCart);
      if (themeStamp !== null) return themeStamp;

      const b2bCart = element?.closest?.('[data-b2b-cart-ui]');
      const b2bStamp = this.readCartTaxesIncludedStamp(b2bCart);
      if (b2bStamp !== null) return b2bStamp;

      const cfg = window.__taxToggleConfig || {};
      if (cfg.pricesIncludeTax === true || cfg.cartTaxesIncluded === true) {
        return true;
      }
      if (cfg.pricesIncludeTax === false || cfg.cartTaxesIncluded === false) {
        return false;
      }
      return false;
    }

    isCartPriceElement(element) {
      return !!element?.closest?.(
        'cart-items-component, .cart-form, .cart-drawer, [data-b2b-cart-ui][data-b2b-catalog-scope="vip"]'
      );
    }

    updatePrice(element, mode, animate) {
      if (!element) return false;
      if (isB2BCartExcludingTaxToggle(element)) return false;

      const isCartPrice = this.isCartPriceElement(element);

      let text = this.sanitizeMoneyText(element.textContent || "");
      if (!text && element.tagName === "TEXT-COMPONENT") {
        text = this.sanitizeMoneyText(element.getAttribute("value") || "");
      }
      const cartVal = this.sanitizeMoneyText(
        element.getAttribute("value") || ""
      );
      if (element.hasAttribute("data-cart-subtotal") && cartVal) {
        if (this.parsePrice(cartVal)) {
          text = cartVal;
        }
      }
      if (!text) return false;

      if (!this.parsePrice(text)) return false;

      let originalPrice = element.getAttribute("data-original-price");
      let currencyAfter = element.getAttribute("data-currency-after");

      /*
       * The JS-rendered B2B/VIP cart drawer stamps each price element with `data-tax-raw` = the raw
       * ex-VAT amount it just painted. That drawer re-paints and re-applies the toggle asynchronously,
       * and `resetCartDerivedPriceAttributes()` clears `data-original-price` between applies — so the
       * normal text-derivation path would re-read text this script had ALREADY multiplied by VAT and
       * cache it as the base, compounding the tax (raw → ×rate → base → ×rate). `data-tax-raw` is
       * authoritative and survives the reset, so we re-derive the SAME ex-VAT base every cycle and the
       * conversion is idempotent. (It's already the ex-VAT base, so `serverMoneyIncludesTax` division
       * never applies here.)
       */
      const rawBaseAttr = element.getAttribute("data-tax-raw");
      const rawBase = rawBaseAttr != null ? this.parsePrice(rawBaseAttr) : "";

      if (rawBase) {
        originalPrice = this.serverMoneyIncludesTax(element)
          ? String(this.toTaxExcludedBase(parseFloat(rawBase)))
          : rawBase;
        element.setAttribute("data-original-price", originalPrice);
      } else if (!originalPrice) {
        let sourceText = text;
        if (
          element.tagName === "TEXT-COMPONENT" &&
          !element.hasAttribute("data-cart-subtotal")
        ) {
          const attrVal = this.sanitizeMoneyText(
            element.getAttribute("value") || ""
          );
          if ((!sourceText || !/\d/.test(sourceText)) && attrVal && /\d/.test(attrVal)) {
            sourceText = attrVal;
          }
        }
        originalPrice = this.parsePrice(sourceText);
        if (!originalPrice) return false;
        if (this.serverMoneyIncludesTax(element)) {
          originalPrice = String(
            this.toTaxExcludedBase(parseFloat(originalPrice))
          );
        }
        element.setAttribute("data-original-price", originalPrice);
      }

      if (currencyAfter == null || currencyAfter === "") {
        const sourceText = text;
        const upper = sourceText.toUpperCase();
        const firstDigit = sourceText.search(/\d/);
        const euroIdx = sourceText.indexOf("€");
        const eurIdx = upper.indexOf("EUR");
        const symbolIndex =
          euroIdx >= 0 && eurIdx >= 0
            ? Math.min(euroIdx, eurIdx)
            : Math.max(euroIdx, eurIdx);
        currencyAfter =
          symbolIndex !== -1 && firstDigit !== -1 && symbolIndex > firstDigit
            ? "true"
            : "false";
        element.setAttribute("data-currency-after", currencyAfter);
      }

      const numPrice = parseFloat(originalPrice);
      if (isNaN(numPrice)) return false;

      let newPrice;
      if (mode === "ttc") {
        /*
         * Non-taxable fee lines (VIP private-cellar insurance + excise/duty: "charge tax" off) are
         * stamped on the total as data-tax-exempt — they carry no VAT, so apply the rate only to the
         * taxable remainder and add the fee back. Without this the inc-VAT total taxes the fee
         * (e.g. €353,50 × 1.23 = €434,81 instead of €350 × 1.23 + €3,50 = €434,00). The attribute is
         * only present on the b2b/VIP total, so every other price keeps the plain × rate path.
         */
        const exemptAttr = element.getAttribute("data-tax-exempt");
        const exempt = exemptAttr != null ? parseFloat(exemptAttr) : NaN;
        if (Number.isFinite(exempt) && exempt > 0 && exempt <= numPrice) {
          newPrice = (numPrice - exempt) * this.taxRate + exempt;
        } else {
          newPrice = numPrice * this.taxRate;
        }
      } else {
        newPrice = numPrice;
      }

      const formattedPrice = this.formatPrice(
        newPrice,
        currencyAfter === "true"
      );

      const currentRendered = this.sanitizeMoneyText(element.textContent || "");
      const roundedCurrent = this.parsePrice(currentRendered);
      const roundedNext = String(Math.round(newPrice * 100) / 100);
      const hasVisualChange = currentRendered !== formattedPrice;
      const hasNumericChange = roundedCurrent !== "" && roundedCurrent === roundedNext;
      const shouldSkipVisualWrite = isCartPrice && (hasVisualChange === false || hasNumericChange);

      if (!shouldSkipVisualWrite && animate) {
        element.classList.add("price-updating");
        setTimeout(() => {
          element.textContent = formattedPrice;
          element.classList.remove("price-updating");
        }, 150);
      } else if (!shouldSkipVisualWrite) {
        element.textContent = formattedPrice;
      }

      if (element.tagName === "TEXT-COMPONENT") {
        const valueText =
          element.hasAttribute("data-cart-subtotal") &&
          this.sanitizeMoneyText(formattedPrice);
        if (valueText) {
          element.setAttribute("value", valueText);
        }
      }

      return true;
    }

    parsePrice(text) {
      let cleaned = this.sanitizeMoneyText(text);
      cleaned = cleaned
        .replace(/^(EUR|USD|GBP|CHF)\s*:?\s*/i, "")
        .replace(/\s*(EUR|USD|GBP|CHF)\s*$/i, "");
      cleaned = cleaned.replace(/[€$\s]/gi, "");
      const lastComma = cleaned.lastIndexOf(",");
      const lastDot = cleaned.lastIndexOf(".");
      // European format: 2.808,00 (dot = thousands, comma = decimal)
      if (lastComma > lastDot) {
        cleaned = cleaned.replace(/\./g, "").replace(",", ".");
      } else {
        // US/UK format: 2,808.00 (comma = thousands, dot = decimal)
        cleaned = cleaned.replace(/,/g, "");
      }
      const num = parseFloat(cleaned);
      return isNaN(num) ? "" : String(num);
    }

    formatPrice(price, symbolAfter) {
      const rounded = Math.round(price * 100) / 100;
      const [intPart, decPart] = rounded.toFixed(2).split(".");
      const withThousands = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      const formatted = `${withThousands},${decPart}`;

      if (symbolAfter) {
        return `${formatted}€`;
      }
      return `€${formatted}`;
    }

    observeDOMChanges() {
      /** @type {ReturnType<typeof setTimeout> | null} */
      this._priceMutationTimer = null;
      /** @type {ReturnType<typeof setTimeout> | null} */
      this._cartServerRefreshTimer = null;

      const observer = new MutationObserver((mutations) => {
        let shouldUpdate = false;
        let shouldReinitDropdown = false;
        let shouldSyncDropdownUI = false;
        let sawCartServerPrice = false;
        let mutationTouchesCartPrices = false;
        mutations.forEach((mutation) => {
          const inspectNodeForCartServerPrice = (node) => {
            if (!(node instanceof Element)) return false;
            const priceNode = node.matches("text-component, .cart__total-value, .cart-items__price, .cart-items__price *")
              ? node
              : node.querySelector?.("text-component, .cart__total-value, .cart-items__price");
            if (!(priceNode instanceof Element)) return false;
            if (
              !priceNode.closest(
                'cart-items-component, .cart-form, .cart-drawer, [data-b2b-cart-ui][data-b2b-catalog-scope="vip"]'
              )
            ) {
              return false;
            }
            const text = this.sanitizeMoneyText(priceNode.textContent || "");
            if (!text || !/\d/.test(text)) return false;
            return !priceNode.hasAttribute("data-original-price");
          };

          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            mutation.addedNodes.forEach((node) => {
              if (this._awaitingCartServerPrices && inspectNodeForCartServerPrice(node)) {
                sawCartServerPrice = true;
              }
              if (node.nodeType === 1) {
                const el = /** @type {Element} */ (node);
                const hasPrice =
                  (el.querySelector &&
                    (el.querySelector(".price") ||
                      el.querySelector(".cart-items__price") ||
                      el.querySelector(".cart__total-value") ||
                      el.querySelector("text-component.cart__total-value"))) ||
                  el.classList?.contains("price");
                if (hasPrice) {
                  shouldUpdate = true;
                  if (
                    el.closest?.(
                      'cart-items-component, .cart-form, .cart-drawer, [data-b2b-cart-ui][data-b2b-catalog-scope="vip"]'
                    )
                  ) {
                    mutationTouchesCartPrices = true;
                  }
                }

                const hasDropdown =
                  node.querySelector &&
                  (node.querySelector(".tax-toggle-dropdown") ||
                    node.querySelector(".tax-toggle-dropdown__current"));
                if (hasDropdown) {
                  if (!this.dropdownInitialized) shouldReinitDropdown = true;
                  shouldSyncDropdownUI = true;
                }
              }
            });
          }
          if (mutation.type === "characterData") {
            const parent = mutation.target?.parentElement;
            if (!parent) return;
            if (this._awaitingCartServerPrices && inspectNodeForCartServerPrice(parent)) {
              sawCartServerPrice = true;
            }
            if (
              parent.matches(".tax-price-label") ||
              parent.matches(".tax-toggle-dropdown__current")
            ) {
              // Tax label text changes are often caused by this script itself; avoid
              // re-entering applyMode and creating visible HT/TTC oscillation.
              shouldSyncDropdownUI = true;
            }
          }
        });

        if (sawCartServerPrice) {
          if (this._cartServerRefreshTimer) {
            clearTimeout(this._cartServerRefreshTimer);
          }
          this._cartServerRefreshTimer = setTimeout(() => {
            this._cartServerRefreshTimer = null;
            this._awaitingCartServerPrices = false;
            if (this._cartRefreshTimer) {
              clearTimeout(this._cartRefreshTimer);
              this._cartRefreshTimer = null;
            }
            if (typeof window !== "undefined" && typeof window.__taxToggleRefreshCartPrices === "function") {
              window.__taxToggleRefreshCartPrices();
            }
          }, 40);
        }

        if (shouldReinitDropdown) {
          this.setupDropdown();
        }

        if (shouldSyncDropdownUI) {
          setTimeout(() => this.updateDropdownUI(), 0);
        }

        if (shouldUpdate) {
          if (
            this._suppressCartPriceMutationUntil &&
            Date.now() < this._suppressCartPriceMutationUntil
          ) {
            return;
          }
          if (this._priceMutationTimer) {
            clearTimeout(this._priceMutationTimer);
          }
          const useCartRefresh = mutationTouchesCartPrices;
          this._priceMutationTimer = setTimeout(() => {
            this._priceMutationTimer = null;
            if (
              useCartRefresh &&
              typeof window !== "undefined" &&
              typeof window.__taxToggleRefreshCartPrices === "function"
            ) {
              window.__taxToggleRefreshCartPrices();
              return;
            }
            this.applyMode(this.currentMode, false);
          }, 120);
        }
      });

      observer.observe(document.body, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
  }

  new TaxToggle();
})();
